// Profil-Nachbau F-25 (PF-GR-saetze-2; output/profil-team-20261014/cockpit-grafiken-20261019/SCHNITTSTELLEN.md 5.4, Entwurf A
// „Bericht in Sätzen“, mittel/seiten.js): die Sätze der übrigen Teile des KI-Cockpits – Nutzung je Person, Nutzung je Team,
// Aufmerksamkeit (Firma und Plattform), Export, Nutzung je Firma und Nutzung je Tarif. Reine Regeln ohne React, ohne Netz, ohne
// Zufall, ohne Betrag: jedes Wort steht in cockpitTexte.ts (Block BERICHT_TEILE und die Blöcke, die die Seiten schon zeigen);
// Beträge der Plattform kommen als fertiger Text herein. Kein Satz nennt eine Person oder eine Rangfolge von Personen; Listen
// behalten die Reihenfolge der Antwort, ausgewählt wird nur ein Höchstwert.
// Die Zeitangabe ist die der übrigen Seiten (seiten.js:57-69, bericht.ts zeitTextSeite, SCHNITTSTELLEN 7a): laufender Monat mit
// „bisher“, das laufende Jahr ab Jahresbeginn.
import type { Bezug, Satz } from './bericht';
import { zeitTextSeite } from './bericht';
import { AUFMERKSAMKEIT, BERICHT_TEILE as W, KENNZAHL, SO_RECHNET, TARIFE, TEAMS } from './cockpitTexte';
import { datum, datumWien, zahl, zahlEineStelle } from './format';
import type { Vorgabe } from './zeitraum';

export type ZeitraumKurz = { von: string; bis: string; tage: number; vorgabe: Vorgabe | null };
const tipp = (kopf: string, text: string): Bezug => ({ art: 'tipp', kopf, text });
/** Eine Folge beginnt nie mit einem Leerzeichen: Teile, die einem anderen folgen, tragen ihres selbst. */
function anfang(satz: Satz): Satz {
  const [erster, ...rest] = satz;
  return typeof erster === 'string' ? [erster.trimStart(), ...rest] : satz;
}
/** Anteil in ganzen Prozent. */
const anteil = (teil: number, ganz: number) => Math.round(teil * 100 / ganz);

// ---- Gemeinsam

const TAG_MS = 86400000;
const UHRZEIT = new Intl.DateTimeFormat('de-AT', { timeZone: 'Europe/Vienna', hour: '2-digit', minute: '2-digit' });
const alsZahl = (tag: string) => Date.UTC(Number(tag.slice(0, 4)), Number(tag.slice(5, 7)) - 1, Number(tag.slice(8, 10)));
const alsTag = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** Zeile über der Aufmerksamkeit: Datum und Uhrzeit in Wien. */
export function standText(iso: string): string {
  return W.stand(datumWien(iso), UHRZEIT.format(new Date(iso)));
}
/** Aufklapper der Vorschau einer Exportdatei, mit oder ohne Zahl der Zeilen. */
export function dateiVorschauText(n?: number): string {
  return W.dateiVorschau(n);
}
/** Der gleich lange Zeitraum davor: von − tage bis von − 1 (Tage ohne Ortszeit). */
export function vorzeitraumVon(z: { von: string; tage: number }): { von: string; bis: string } {
  const von = alsZahl(z.von);
  return { von: alsTag(von - z.tage * TAG_MS), bis: alsTag(von - TAG_MS) };
}

// ---- Nutzung je Person (Entwurf seiten.js:138-145, 217-240)

export type Stufe = { index: number; von: number; bis: number | null; text: string; anzahl: number };
/** Säulen der Häufigkeit: zuerst 0, dann Stufen zu 2, 5, 10 oder 20 Recherchen; mehr als sechs: die sechste reicht nach oben. */
export function stufen(recherchen: number[]): Stufe[] {
  const max = Math.max(0, ...recherchen);
  const schritt = max <= 8 ? 2 : max <= 25 ? 5 : max <= 60 ? 10 : 20;
  const grenzen: { von: number; bis: number | null; text: string }[] = [{ von: 0, bis: 0, text: '0' }];
  for (let a = 1; a <= max; a += schritt) {
    const bis = a + schritt - 1;
    grenzen.push({ von: a, bis, text: a === bis ? `${a}` : `${a}–${bis}` });
  }
  if (grenzen.length > 6) grenzen.splice(5, grenzen.length - 5, { von: grenzen[5].von, bis: null, text: `ab ${grenzen[5].von}` });
  return grenzen.map((g, index) => ({ index, ...g, anzahl: recherchen.filter(w => w >= g.von && (g.bis === null || w <= g.bis)).length }));
}
/** Index der Stufe, in die ein Wert fällt; -1 ohne passende Stufe. */
export function stufeVon(st: Stufe[], recherchen: number): number {
  return st.find(s => recherchen >= s.von && (s.bis === null || recherchen <= s.bis))?.index ?? -1;
}

export type PersonenLage = { zeitraum: ZeitraumKurz; produkt: string; aktive: number; personen: number; zuWenige: boolean;
  mindestens: number; liste: { recherchen: number; nachfragen: number }[] };
export function personenSaetze(l: PersonenLage): { satz: Satz; folge: Satz; grafik: string; stufen: Stufe[] } {
  const zeit = zeitTextSeite(l.zeitraum);
  const liste = l.zuWenige ? [] : l.liste;
  const st = stufen(liste.map(p => p.recherchen));
  const summe = liste.reduce((s, p) => s + p.recherchen, 0);
  const satz: Satz = l.aktive === 0
    ? [l.zeitraum.vorgabe === 'heute' ? W.person.niemandHeute : W.person.niemand(zeit)]
    : [W.person.vor(zeit, l.aktive === 1),
      { text: l.aktive >= l.personen ? (l.personen === 1 ? W.person.einzige : W.person.alle(l.personen)) : W.person.von(l.aktive, l.personen),
        bezug: tipp(KENNZAHL.aktivePersonen, SO_RECHNET.aktivTipp) },
      W.person.nach(l.produkt)];
  const grafik = l.zuWenige ? W.person.grafikZuWenige(l.mindestens) : summe === 0 ? W.person.keineRecherchen : W.person.grafik;
  if (l.aktive === 0) return { satz, folge: [], grafik, stufen: st };
  if (l.zuWenige) return { satz, folge: [W.person.zuWenige(l.produkt, l.mindestens)], grafik, stufen: st };
  if (summe === 0) return { satz, folge: [W.person.keineRecherchen], grafik, stufen: st };
  const hoechst = Math.max(...liste.map(p => p.recherchen)) / summe;
  const folge: Satz = [hoechst <= 0.25 ? W.person.breit : W.person.eine(Math.round(hoechst * 100))];
  // Die Stufe mit den meisten Personen außer 0, bei Gleichstand die erste.
  const groesste = st.slice(1).reduce<Stufe | null>((x, y) => x === null || y.anzahl > x.anzahl ? y : x, null);
  if (groesste !== null && groesste.anzahl > 1)
    folge.push(W.person.stufeVor(groesste.anzahl),
      { text: W.person.mal(groesste.von, groesste.bis), bezug: { art: 'stufe', index: groesste.index }, stark: true }, '.');
  const ohne = st[0].anzahl;
  if (ohne > 0) {
    // „nichts gefragt“ nur, wenn auch keine dieser Personen eine Nachfrage gestellt hat (E-3).
    const nichts = liste.every(p => p.recherchen > 0 || p.nachfragen === 0);
    folge.push(' ', { text: W.personen(ohne), bezug: { art: 'stufe', index: 0 }, stark: true }, W.person.ohneRecherche(ohne, nichts));
  }
  return { satz, folge: anfang(folge), grafik, stufen: st };
}

// ---- Nutzung je Team (Entwurf seiten.js:288-317)

export type TeamZeileKurz = { name: string; personenHeute: number | null; aktivePersonen: number | null; recherchen: number | null; zuWenige?: true };
export type TeamLage = { produkt: string; mindestens: number; namen: boolean; teams: TeamZeileKurz[]; ohneTeam: TeamZeileKurz | null;
  geloeschte: TeamZeileKurz | null; ganzeFirma: { personenHeute: number | null; aktivePersonen: number | null; recherchen: number | null } };
/** Die Zeilen der Tabelle: Teams, „Ohne eigenes Team“, „Gelöschte Teams“, in dieser Reihenfolge. */
const teamZeilen = (l: TeamLage) => [...l.teams, ...(l.ohneTeam ? [l.ohneTeam] : []), ...(l.geloeschte ? [l.geloeschte] : [])];
const mitZahlen = (z: TeamZeileKurz) => !z.zuWenige && z.recherchen !== null;
const personenOhneTeam = (l: TeamLage) => l.ohneTeam?.personenHeute ?? 0;
const personenImTeam = (l: TeamLage) => (l.ganzeFirma.personenHeute ?? 0) - personenOhneTeam(l);

export function teamSaetze(l: TeamLage): { satz: Satz; folge: Satz } {
  const k = l.teams.length, m = personenOhneTeam(l);
  const satz: Satz = k === 0
    ? [W.team.keinTeam, ...(l.geloeschte ? [W.team.geloeschte] : [])]
    : [k === 1 ? W.team.einTeam(l.teams[0].name, l.teams[0].personenHeute ?? 0) : W.team.mehrTeams(k, personenImTeam(l)),
      m > 0 ? W.team.ohne(m) : W.team.alleImTeam];
  const ganz = l.ganzeFirma.recherchen;
  if (ganz === null || ganz === 0) return { satz, folge: [TEAMS.leer] };
  const zeilen = teamZeilen(l);
  const zahlen = zeilen.filter(mitZahlen), verdeckt = zeilen.filter(z => z.zuWenige);
  if (zahlen.length === 0 && verdeckt.length > 0)
    return { satz, folge: [W.team.schutzVor(l.produkt, l.mindestens),
      { text: W.team.schutzBezug(l.ganzeFirma.aktivePersonen ?? 0, ganz), bezug: tipp(W.team.schutzTippKopf, W.team.schutzTipp(l.mindestens)), stark: true },
      '.'] };
  const folge: Satz = [];
  // Die Gruppe mit den meisten Recherchen (Entwurf seiten.js:298), nur aus Zeilen mit Zahlen, bei Gleichstand die erste (E-4).
  const top = zahlen.reduce<TeamZeileKurz | null>((x, y) => x === null || (y.recherchen ?? 0) > (x.recherchen ?? 0) ? y : x, null);
  if (top !== null && (top.recherchen ?? 0) > 0) {
    const r = top.recherchen ?? 0, aktiv = top.aktivePersonen ?? 0;
    folge.push(W.team.groesste(top.name, aktiv, anteil(r, ganz), aktiv > 0 ? Math.round(r / aktiv) : null));
  }
  folge.push(verdeckt.length > 0 ? W.team.verdeckt(verdeckt.length) : l.namen ? W.team.mitNamen : W.team.alleMitZahlen(l.mindestens));
  return { satz, folge: anfang(folge) };
}
/** Balken der Zeilen mit Zahlen: Länge im Verhältnis zur größten Zeile, Text mit dem Anteil an der ganzen Firma. */
export function teamBalken(l: TeamLage): { key: string; name: string; recherchen: number; prozent: number; text: string }[] {
  const zeilen = teamZeilen(l).filter(mitZahlen);
  const groesste = Math.max(0, ...zeilen.map(z => z.recherchen ?? 0));
  const ganz = l.ganzeFirma.recherchen ?? 0;
  return zeilen.map(z => {
    const r = z.recherchen ?? 0;
    return { key: z.name, name: z.name, recherchen: r, prozent: groesste > 0 ? r * 100 / groesste : 0,
      text: W.team.balkenText(zahl(r), ganz > 0 ? anteil(r, ganz) : 0) };
  });
}
/** „So ist Ihre Firma aufgeteilt“: Personen heute in einem Team und ohne eigenes Team; Felder je Person bis 60 Personen. */
export function aufteilungText(l: TeamLage): { imTeam: number; ohne: number; eins: string; zwei: string; label: string; felder: boolean } {
  const imTeam = personenImTeam(l), ohne = personenOhneTeam(l);
  return {
    imTeam, ohne,
    eins: l.teams.length === 1 ? W.team.legendeTeam(l.teams[0].name, imTeam) : W.team.legendeImTeam(imTeam),
    zwei: W.team.legendeOhne(ohne),
    label: W.team.aufteilungLabel(imTeam, ohne),
    felder: (l.ganzeFirma.personenHeute ?? 0) <= 60,
  };
}

// ---- Aufmerksamkeit (Entwurf seiten.js:382-394, 649-669)

/** grenze: null ohne Recht auf die persönlichen Grenzen; kontingent: undefined ohne Recht, null ohne Hinweis. */
export type AufmerksamkeitFirmaLage = { inaktiv: number; namen: boolean; grenze: number | null;
  kontingent: { prozent: string; monat: string; inRecherchen?: boolean } | null | undefined };
export function aufmerksamkeitFirmaSaetze(l: AufmerksamkeitFirmaLage): { satz: Satz; folge: Satz; hinweise: number; pruefungen: number } {
  const grenze = l.grenze ?? 0;
  const hinweise = (l.inaktiv > 0 ? 1 : 0) + (grenze > 0 ? 1 : 0) + (l.kontingent ? 1 : 0);
  const pruefungen = 1 + (l.grenze !== null ? 1 : 0) + (l.kontingent !== undefined ? 1 : 0);
  const satz: Satz = [hinweise === 0 ? AUFMERKSAMKEIT.nichts : hinweise === 1 ? W.aufmerksamkeit.eine : W.aufmerksamkeit.mehrere(hinweise)];
  const folge: Satz = [];
  if (hinweise === 0) return { satz, folge, hinweise, pruefungen };
  if (l.inaktiv > 0)
    folge.push({ text: W.personen(l.inaktiv), bezug: tipp(AUFMERKSAMKEIT.inaktiv, l.namen ? W.aufmerksamkeit.inaktivTippMit : W.aufmerksamkeit.inaktivTippOhne), stark: true },
      W.aufmerksamkeit.inaktivNach(l.inaktiv));
  if (grenze > 0) folge.push(' ' + AUFMERKSAMKEIT.grenze(grenze));
  if (l.kontingent) folge.push(' ' + AUFMERKSAMKEIT.firmaKontingent(l.kontingent.prozent, l.kontingent.monat, l.kontingent.inRecherchen));
  if (hinweise < pruefungen) folge.push(W.aufmerksamkeit.rest);
  return { satz, folge: anfang(folge), hinweise, pruefungen };
}

export type AufmerksamkeitPlattformLage = { heute: string; zahlung: { name: string; seit: string | null; ohneVertrag?: boolean }[];
  kontingent: { name: string; prozent: string }[]; inaktiv: { name: string }[]; testphase: { name: string; endet: string }[] };
/** Die eine Firma mit ihrem Grund (Satzteil mit führendem Leerzeichen), wenn es genau einen Hinweis gibt. */
function einzigerHinweis(l: AufmerksamkeitPlattformLage): { name: string; grund: string } {
  if (l.zahlung.length > 0) return { name: l.zahlung[0].name, grund: W.plattform.grundZahlung };
  if (l.kontingent.length > 0) return { name: l.kontingent[0].name, grund: W.plattform.grundKontingent(l.kontingent[0].prozent) };
  if (l.inaktiv.length > 0) return { name: l.inaktiv[0].name, grund: W.plattform.grundInaktiv };
  return { name: l.testphase[0].name, grund: W.plattform.grundTestphase(datumWien(l.testphase[0].endet)) };
}
export function aufmerksamkeitPlattformSaetze(l: AufmerksamkeitPlattformLage): { satz: Satz; folge: Satz } {
  // Die vier Prüfungen in der Reihenfolge der Folge: Zahlung, Kontingent, Testphase, inaktiv.
  const pruefungen = [
    { art: 'zahlung', titel: AUFMERKSAMKEIT.zahlung, anzahl: l.zahlung.length },
    { art: 'kontingent', titel: AUFMERKSAMKEIT.kontingent, anzahl: l.kontingent.length },
    { art: 'testphase', titel: AUFMERKSAMKEIT.testphase, anzahl: l.testphase.length },
    { art: 'inaktiv', titel: AUFMERKSAMKEIT.inaktiv, anzahl: l.inaktiv.length },
  ];
  const n = pruefungen.reduce((s, p) => s + p.anzahl, 0);
  if (n === 0) return { satz: [W.plattform.keine], folge: [W.plattform.alleOk] };
  let satz: Satz;
  if (n === 1) {
    const { name, grund } = einzigerHinweis(l);
    satz = [W.plattform.eineVor, { text: `„${name}“`, bezug: tipp(name, name + grund), stark: false }, grund];
  } else satz = [W.plattform.mehrere(n)];
  const folge: Satz = [];
  if (n === 1 && l.zahlung.length === 1) {
    const { seit, ohneVertrag } = l.zahlung[0];
    folge.push(W.plattform.seit(seit === null ? null : datumWien(seit) === datum(l.heute) ? 'heute' : datumWien(seit), ohneVertrag === true));
  } else if (n >= 2) folge.push(pruefungen.filter(p => p.anzahl > 0).map(p => W.plattform.anzahl(p.titel, p.anzahl)).join('; ') + '.');
  const leer = pruefungen.filter(p => p.anzahl === 0);
  if (leer.length > 0) folge.push(' ' + W.plattform.istUnauffaellig(leer.map(p => W.plattform.unauffaellig[p.art])));
  return { satz, folge: anfang(folge) };
}

// ---- Export (Entwurf seiten.js:435-438)

/** Die Plattform übergibt ihren Satz fertig (EXPORT.satzPlattform, der einzige Satz mit Währung). */
export function exportSaetze(art: 'firma' | 'plattform', z: { von: string; bis: string }, plattformSatz?: string): { satz: Satz; folge: Satz } {
  if (art === 'firma') return { satz: [W.export.firmaSatz(datum(z.von), datum(z.bis))], folge: [W.export.firmaFolge] };
  return { satz: [plattformSatz ?? ''], folge: [W.export.plattformFolge] };
}

// ---- Nutzung je Firma (Entwurf seiten.js:554-563)

/** geld nur mit dem Recht „Kostenbericht ansehen“, als fertiger Text der Plattform. */
export type FirmenLage = { aktiv: number; mitZugang: number; top?: { name: string; recherchen: number; nachfragen: number };
  geld?: { kosten: string; umsatz: string; umsatzNull: boolean } };
export function firmenSaetze(l: FirmenLage): { satz: Satz; folge: Satz } {
  const m = l.mitZugang, a = l.aktiv, top = l.top;
  const rest: Satz = top
    ? [': ', { text: top.name, bezug: tipp(top.name, W.firmen.topTipp(zahl(top.recherchen), zahl(top.nachfragen))) },
      W.firmen.mit(zahl(top.recherchen), top.recherchen === 1)]
    : ['.'];
  const satz: Satz = m === 0 ? [W.firmen.keineMitZugang]
    : a === 0 ? [m === 1 ? W.firmen.einzigeNicht : W.firmen.keine(m)]
    : a === m && m === 1 ? [W.firmen.einzige, ...rest]
    : a === m ? [W.firmen.alle(m)]
    : a === 1 ? [W.firmen.nurEine(m), ...rest]
    : [W.firmen.einige(m, a)];
  const saetze: string[] = [];
  if (m - a > 0) saetze.push(W.firmen.still(m - a));
  if (l.geld) saetze.push(l.geld.umsatzNull ? W.firmen.ohneUmsatz(l.geld.kosten) : W.firmen.mitUmsatz(l.geld.kosten, l.geld.umsatz));
  return { satz, folge: saetze.length > 0 ? [saetze.join(' ')] : [] };
}

// ---- Nutzung je Tarif (Entwurf seiten.js:602-615)

export type TarifKurz = { name: string; firmen: number; plaetze: number; jePlatz: number | null; davor?: number | null };
/** Beträge als fertiger Text der Plattform; marge.wert nur für den Vergleich der Tarife. */
export type TarifGeld = { name: string; kosten: string; umsatz: string; umsatzNull: boolean; marge: { wert: number; text: string } };
function tarifSatz(tarife: TarifKurz[], ohneVergleich: boolean): Satz {
  if (tarife.length === 0) return [TARIFE.leer];
  if (tarife.length === 1) {
    const t = tarife[0];
    const kopf = t.firmen === 1 ? W.tarife.eineFirma(t.name) : W.tarife.alleFirmen(t.firmen, t.name);
    if (t.jePlatz === null) return [kopf, W.tarife.keinPlatz];
    const davor = typeof t.davor === 'number' && !ohneVergleich ? zahlEineStelle(t.davor) : null;
    return [kopf, W.tarife.jePlatzVor,
      { text: W.tarife.recherchen(zahlEineStelle(t.jePlatz)), bezug: tipp(W.tarife.tippKopf, W.tarife.tipp(t.plaetze, davor)) },
      davor === null ? '.' : W.tarife.davor(davor)];
  }
  const werte = tarife.flatMap(t => t.jePlatz === null ? [] : [{ name: t.name, wert: t.jePlatz }]);
  if (werte.length === 0) return [W.tarife.keinerBelegt];
  const hoch = werte.reduce((x, y) => y.wert > x.wert ? y : x), tief = werte.reduce((x, y) => y.wert < x.wert ? y : x);
  const hochText = zahlEineStelle(hoch.wert), tiefText = zahlEineStelle(tief.wert);
  // Gleich heißt: gleich, wie es dasteht (eine Nachkommastelle), sonst stünde „zwischen 4,5 und 4,5“.
  if (hochText === tiefText) return [W.tarife.gleich(hochText)];
  if (tief.wert > 0 && hoch.wert / tief.wert >= 1.5)
    return [W.tarife.faktor(hoch.name, zahlEineStelle(hoch.wert / tief.wert), tief.name, hochText, tiefText)];
  return [W.tarife.spanne(tiefText, hochText)];
}
function tarifFolge(geld: TarifGeld[]): Satz {
  if (geld.length === 0) return [];
  if (geld.length === 1) {
    const g = geld[0];
    return [g.umsatzNull ? W.tarife.ohneUmsatz(g.kosten, g.marge.text) : W.tarife.mitUmsatz(g.kosten, g.umsatz, g.marge.text)];
  }
  if (geld.every(g => g.marge.wert >= 0)) {
    const beste = geld.reduce((x, y) => y.marge.wert > x.marge.wert ? y : x);
    return [W.tarife.alleTragen(geld.length, beste.name, beste.marge.text)];
  }
  const schlechteste = geld.reduce((x, y) => y.marge.wert < x.marge.wert ? y : x);
  return [W.tarife.traegtNicht(schlechteste.name, schlechteste.marge.text)];
}
export function tarifeSaetze(l: { tarife: TarifKurz[]; geld?: TarifGeld[]; ohneVergleich: boolean }): { satz: Satz; folge: Satz } {
  return { satz: tarifSatz(l.tarife, l.ohneVergleich), folge: tarifFolge(l.geld ?? []) };
}
